"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function TermsAndConditions() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const portrait1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const portrait2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const terms = [{
    id: "item-1",
    title: "01. Research Use Only",
    content: "All products sold by Iron Habit Peptides are strictly for laboratory research and analytical purposes. They are not intended for human consumption, medical use, or therapeutic applications. By purchasing, you acknowledge that these compounds are handled by qualified professionals in a controlled environment."
  }, {
    id: "item-2",
    title: "02. Purity & Verification",
    content: "We guarantee a minimum purity level as specified in our Certificates of Analysis (COA). Every batch undergoes third-party testing to ensure molecular integrity. Discrepancies in research results due to improper handling or storage at the destination facility are not the responsibility of Iron Habit Peptides."
  }, {
    id: "item-3",
    title: "03. Storage & Stability",
    content: "Peptides are sensitive to temperature and light. Our products are stored in climate-controlled environments until dispatch. It is the researcher's responsibility to maintain optimal storage conditions (typically -20°C for long-term stability) upon receipt to prevent degradation."
  }, {
    id: "item-4",
    title: "04. Compliance & Legal Use",
    content: "The buyer is responsible for complying with all local, state, and federal regulations regarding the acquisition and use of research compounds. Iron Habit Peptides does not provide legal advice and assumes no liability for the misuse of its products."
  }, {
    id: "item-5",
    title: "05. Ordering & Payment",
    content: "All orders are subject to acceptance and availability. We reserve the right to refuse service to anyone suspected of intending to use our products for non-research purposes. Payments must be cleared before any shipment is initiated."
  }, {
    id: "item-6",
    title: "06. Shipping & Delivery",
    content: "We utilize specialized packaging to maintain stability during transit. While we strive for timely delivery, we are not liable for delays caused by customs or third-party carriers. Risk of loss passes to the buyer upon delivery to the carrier."
  }, {
    id: "item-7",
    title: "07. Returns & Refunds",
    content: "Due to the sensitive nature of research compounds and the requirement for strict chain-of-custody, we cannot accept returns once a product has left our facility. Refunds are only issued in cases of proven shipping errors or verified quality defects."
  }, {
    id: "item-8",
    title: "08. Intellectual Property",
    content: "All content on this site, including text, graphics, and logos, is the property of Iron Habit Peptides. Unauthorized use of our branding or technical documentation is strictly prohibited."
  }, {
    id: "item-9",
    title: "09. Limitation of Liability",
    content: "Iron Habit Peptides shall not be liable for any special or consequential damages that result from the use of, or the inability to use, the materials on this site or the performance of the products, even if advised of the possibility of such damages."
  }, {
    id: "item-10",
    title: "10. Amendments to Terms",
    content: "We reserve the right to update these terms and conditions at any time without prior notice. Continued use of our services constitutes acceptance of the revised terms. Researchers are encouraged to review this section periodically."
  }];
  return <section data-section-id="1805" id="termsandconditions" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 relative">
        <div aria-hidden="true" className="pointer-events-none absolute top-12 left-4 md:left-12 select-none italic font-serif leading-none" style={{
        fontSize: "clamp(12rem, 22vw, 21rem)",
        color: "var(--foreground)",
        opacity: 0.04
      }}>
          02
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 relative">
          <div className="lg:col-span-8 relative">
            <div ref={eyebrow.ref} className={`flex items-center gap-4 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-default uppercase !text-[rgba(251,189,35,1)]" style={{
              fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
              letterSpacing: "0.22em",
              color: "var(--primary)",
              lineHeight: 1
            }}> LEGAL — TERMS & CONDITIONS</span>
              <div className="hidden sm:block h-px flex-1 max-w-[240px]" style={{
              background: "linear-gradient(to right, var(--primary), transparent)"
            }} />
              <span aria-hidden="true" className="hidden w-1.5 h-1.5 rounded-lg !bg-[rgba(251,189,35,1)] sm:inline-block" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <h2 ref={heading.ref} className={`font-default font-light mt-8 transition-all duration-1000 ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
            letterSpacing: "-0.025em",
            lineHeight: 1.02
          }}>
              The framework of{" "}
              <span className="italic transition-opacity duration-700 delay-200 opacity-100 !text-[rgba(251,189,35,1)]" style={{
              color: "var(--primary)"
            }}>
                integrity
              </span>{" "}
              and research.
            </h2>

            <div className="flex items-center gap-3 mt-10 mb-12">
              <div className="h-px w-40" style={{
              background: "linear-gradient(to right, var(--primary), transparent)"
            }} />
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg !bg-[rgba(251,189,35,1)]" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <div className="mt-12">
              <Accordion type="single" collapsible className="w-full space-y-4">
                {terms.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border/40">
                    <AccordionTrigger className="text-left font-default py-6 hover:no-underline group">
                      <span className="text-lg font-light tracking-tight group-data-[state=open]:text-primary transition-colors !text-[rgba(251,189,35,1)] md:text-xl">
                        {item.title}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <p className="font-default text-muted-foreground leading-relaxed max-w-[65ch] text-base md:text-lg">
                        {item.content}
                      </p>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="mt-16 flex items-center gap-4">
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg !bg-[rgba(251,189,35,1)]" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-default uppercase" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.14em",
              color: "var(--muted-foreground)"
            }}>IRON HABIT PEPTIDES · LEGAL DEPARTMENT · EST. 2026</span>
            </div>
          </div>

          
        </div>
      </div>
    </section>;
}